import { useNavigate } from 'react-router-dom'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import {
  ClipboardList,
  Download,
  ExternalLink,
  FileText,
  FileWarning,
  Loader2,
  Sparkles,
  Upload,
} from 'lucide-react'
import { api, messageErreur } from '../lib/api'
import { Chargement, Message, Modale, Pastille } from './base'
import * as f from '../lib/format'
import { ORIGINES_DOCUMENT, PRECISIONS_DATE, TYPES_DOCUMENT } from '@doc/shared'

export type DocumentDetail = {
  id: string
  titre: string
  type: keyof typeof TYPES_DOCUMENT
  origine: keyof typeof ORIGINES_DOCUMENT
  dateDocument: string | null
  precisionDateDocument: keyof typeof PRECISIONS_DATE
  dateAjout: string
  nomFichierOriginal: string | null
  mimeType: string | null
  tailleOctets: number | null
  nbPages: number | null
  ocrApplique: boolean
  texteSource: string | null
  aUnFichier: boolean
  fichierPresent: boolean
  nbDonneesEtayees: number
  deposePar: { nom: string; prenom: string } | null
  patient: { id: string; reference: string; nom: string | null; prenom: string | null }
  analyses?: { id: string; etat: string; erreur: string | null }[]
}

export default function VisionneuseDocument({
  documentId,
  page,
  passage,
  fermer,
}: {
  documentId: string
  page?: number | null
  passage?: string | null
  fermer: () => void
}) {
  const naviguer = useNavigate()
  const { data, isLoading } = useQuery({
    queryKey: ['document', documentId],
    queryFn: () => api<{ document: DocumentDetail }>(`/api/documents/${documentId}`),
  })

  const doc = data?.document
  const url = `/api/documents/${documentId}/fichier`
  const derniere = doc?.analyses?.[0]

  // Un document déposé avant qu'on sache le lire peut être analysé après coup.
  const analyser = useMutation({
    mutationFn: () =>
      api<{ analyse: { id: string } }>(`/api/documents/${documentId}/analyser`, {
        methode: 'POST',
        corps: {},
      }),
    onSuccess: ({ analyse }) => {
      fermer()
      naviguer(`/verification/${analyse.id}`)
    },
  })

  return (
    <Modale
      titre={doc?.titre ?? 'Document'}
      large
      sousTitre={
        doc && (
          <span className="flex flex-wrap items-center gap-x-3 gap-y-1">
            <span>{TYPES_DOCUMENT[doc.type]}</span>
            <span>·</span>
            <span>
              {doc.dateDocument
                ? f.dateSelonPrecision(doc.dateDocument, doc.precisionDateDocument)
                : 'date du document inconnue'}
            </span>
            <span>·</span>
            <span>déposé {f.ilYA(doc.dateAjout)}</span>
            <Pastille ton="bleu">{ORIGINES_DOCUMENT[doc.origine]}</Pastille>
            {doc.ocrApplique && <Pastille>texte reconnu automatiquement</Pastille>}
          </span>
        )
      }
      fermer={fermer}
      piedDePage={
        doc && (
          <>
            {doc.aUnFichier && !doc.fichierPresent ? null : derniere &&
              derniere.etat === 'A_VERIFIER' ? (
              <button
                className="bouton-doux mr-auto"
                onClick={() => {
                  fermer()
                  naviguer(`/verification/${derniere.id}`)
                }}
              >
                <ClipboardList className="h-4 w-4" />
                Vérifier ce qui a été extrait
              </button>
            ) : (
              <button
                className="bouton-doux mr-auto"
                onClick={() => analyser.mutate()}
                disabled={analyser.isPending || derniere?.etat === 'EN_COURS' || derniere?.etat === 'EN_ATTENTE'}
                title="Lire le document et proposer ce qu'il contient"
              >
                {analyser.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Sparkles className="h-4 w-4" />
                )}
                {derniere ? 'Relancer l’analyse' : 'Analyser ce document'}
              </button>
            )}

            {doc.aUnFichier && doc.fichierPresent && (
              <>
                <a className="bouton-discret" href={url} target="_blank" rel="noreferrer">
                  <ExternalLink className="h-4 w-4" />
                  Ouvrir dans un onglet
                </a>
                <a className="bouton-doux" href={`${url}?telecharger=1`}>
                  <Download className="h-4 w-4" />
                  Télécharger
                </a>
              </>
            )}
            <button className="bouton-primaire" onClick={fermer}>
              Fermer
            </button>
          </>
        )
      }
    >
      {isLoading || !doc ? (
        <Chargement />
      ) : (
        <div className="flex flex-col gap-3">
          {passage && (
            <Message ton="info">
              <p className="font-medium">
                Passage justificatif{page ? `, page ${page}` : ''} :
              </p>
              <p className="mt-0.5 italic">« {passage} »</p>
            </Message>
          )}

          {doc.nbDonneesEtayees > 0 && (
            <p className="text-[12.5px] text-brume">
              Ce document est la source de {doc.nbDonneesEtayees} information
              {doc.nbDonneesEtayees > 1 ? 's' : ''} du dossier.
            </p>
          )}

          <Apercu doc={doc} url={url} />

          <dl className="grid grid-cols-2 gap-x-6 gap-y-2 border-t border-trait pt-3 text-[12.5px]">
            <Ligne terme="Fichier d’origine" valeur={doc.nomFichierOriginal ?? '—'} />
            <Ligne terme="Format" valeur={doc.mimeType ?? 'texte collé'} />
            <Ligne terme="Taille" valeur={f.octets(doc.tailleOctets)} />
            <Ligne terme="Pages" valeur={doc.nbPages ? String(doc.nbPages) : '—'} />
            <Ligne
              terme="Déposé par"
              valeur={doc.deposePar ? `${doc.deposePar.prenom} ${doc.deposePar.nom}` : '—'}
            />
            <Ligne terme="Date de dépôt" valeur={f.heure(doc.dateAjout)} />
          </dl>
        </div>
      )}
    </Modale>
  )
}

export function Apercu({ doc, url }: { doc: DocumentDetail; url: string }) {
  if (doc.aUnFichier && !doc.fichierPresent) {
    return <FichierManquant doc={doc} />
  }

  if (doc.texteSource) {
    return (
      <pre className="max-h-[50vh] overflow-auto rounded-lg bg-slate-50 p-4 text-[13px] leading-relaxed whitespace-pre-wrap text-encre">
        {doc.texteSource}
      </pre>
    )
  }

  if (doc.mimeType === 'application/pdf') {
    return <iframe src={url} title={doc.titre} className="h-[60vh] w-full rounded-lg bg-slate-50" />
  }

  if (doc.mimeType?.startsWith('image/')) {
    return (
      <img
        src={url}
        alt={doc.titre}
        className="max-h-[60vh] w-full rounded-lg bg-slate-50 object-contain"
      />
    )
  }

  // Word, e-mail : pas d'aperçu dans le navigateur, on le dit plutôt que
  // d'afficher un cadre vide.
  return (
    <div className="flex items-center gap-3 rounded-lg bg-slate-50 px-4 py-6 text-[13px] text-ardoise">
      <FileText className="h-5 w-5 text-brume" />
      Ce format ne s’affiche pas dans le navigateur. Le fichier d’origine est conservé : il peut
      être téléchargé.
    </div>
  )
}

/**
 * Le fichier d'origine a disparu du serveur. Les informations du document
 * restent, mais le fichier doit être redéposé pour être relu.
 */
function FichierManquant({ doc }: { doc: DocumentDetail }) {
  const client = useQueryClient()
  const [erreur, setErreur] = useState<string | null>(null)
  const [enCours, setEnCours] = useState(false)
  const [resultat, setResultat] = useState<string | null>(null)

  const redeposer = async (fichier: File) => {
    setErreur(null)
    setEnCours(true)
    try {
      const formulaire = new FormData()
      formulaire.append('fichier', fichier)
      const reponse = await api<{ memeFichier: boolean | null }>(
        `/api/documents/${doc.id}/fichier`,
        { methode: 'POST', formulaire },
      )
      setResultat(
        reponse.memeFichier === true
          ? "C'est bien le même fichier qu'à l'origine : l'empreinte correspond."
          : reponse.memeFichier === false
            ? "Le fichier déposé n'est pas identique à celui d'origine. Il est conservé tel quel."
            : 'Fichier enregistré.',
      )
      client.invalidateQueries({ queryKey: ['document', doc.id] })
      client.invalidateQueries({ queryKey: ['dossier'] })
    } catch (e) {
      setErreur(messageErreur(e))
    } finally {
      setEnCours(false)
    }
  }

  return (
    <div className="flex flex-col gap-3 rounded-lg bg-attention-fond px-4 py-5">
      <div className="flex items-start gap-3">
        <FileWarning className="mt-0.5 h-5 w-5 shrink-0 text-attention" />
        <div className="text-[13px] text-encre">
          <p className="font-medium text-attention">
            Le fichier d’origine n’est plus présent sur le serveur.
          </p>
          <p className="mt-1">
            Les informations du document sont conservées : {doc.nomFichierOriginal ?? 'le fichier'}
            {doc.tailleOctets ? `, ${f.octets(doc.tailleOctets)}` : ''}
            {doc.nbPages ? `, ${doc.nbPages} pages` : ''}. Redépose-le pour pouvoir le relire et
            l’analyser.
          </p>
        </div>
      </div>

      {resultat ? (
        <p className="text-[13px] text-valide">{resultat}</p>
      ) : (
        <label className="bouton-doux w-fit cursor-pointer">
          {enCours ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
          Redéposer le fichier d’origine
          <input
            type="file"
            className="hidden"
            accept=".pdf,.jpg,.jpeg,.png,.webp,.heic,.tif,.tiff,.doc,.docx,.rtf,.txt,.eml,.msg"
            onChange={(e) => {
              const fichier = e.target.files?.[0]
              if (fichier) void redeposer(fichier)
            }}
          />
        </label>
      )}

      {erreur && <Message ton="critique">{erreur}</Message>}
    </div>
  )
}

function Ligne({ terme, valeur }: { terme: string; valeur: string }) {
  return (
    <div>
      <dt className="text-brume">{terme}</dt>
      <dd className="text-encre">{valeur}</dd>
    </div>
  )
}
